"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useForm = void 0;
const react_1 = require("react");
const useFormErrors_1 = require("./useFormErrors");
const useLocalContext_1 = require("./useLocalContext");
const useProxy_1 = require("./useProxy");
const errors_1 = require("./errors");
const utils_1 = require("./utils");
/*
 * Create a new form instance.
 *
 * This simple hook uses JS proxies to prevent the need to know which
 * fields we might need ahead of time. The proxy for updating form
 * data values returns an updater function on-demand for the requested
 * property. It's prety neat.
 *
 * Usage looks something like this:
 *
 *   const initialValues = {first: 1, second: 2}
 *   const {fieldProps} = useForm({initialValues})
 *   return (
 *     <>
 *       <input {...fieldProps.first} />
 *       <input {...fieldProps.second} />
 *     </>
 *   )
 */
function useForm(args = {}) {
    const { initialValues = {}, onSubmit, onChange, onError = utils_1.identity, validator, noPositive, name, } = args;
    const [values, setValues] = (0, react_1.useState)(initialValues);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const ctx = (0, useLocalContext_1.useLocalContext)({ values, onChange });
    const { errors, updateErrors, validating, updateValidating, touched, positive, handleBlur, hasErrors, hasFieldErrors, anyValidating, setErrors, } = (0, useFormErrors_1.useFormErrors)({ formContext: ctx, onError, validator, name });
    const valuesProxy = new Proxy(values, {
        get(target, name) {
            return target[name] !== undefined ? target[name] : '';
        },
    });
    function updateValues(changedValues) {
        var _a;
        const newValues = Object.assign(Object.assign({}, ctx.values), changedValues);
        setValues(newValues);
        (_a = ctx.onChange) === null || _a === void 0 ? void 0 : _a.call(ctx, newValues);
    }
    const updateValuesProxy = (0, useProxy_1.useProxy)(updateValues, name => (0, utils_1.handleEvent)(value => updateValues({ [name]: value })));
    function submit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (onSubmit) {
                setLoading(true);
                setErrors({}); // Clear any disconnected form errors
                try {
                    yield onSubmit(values);
                }
                catch (e) {
                    setErrors((0, errors_1.normalizeServerErrors)(e));
                    throw e;
                }
                finally {
                    setLoading(false);
                }
            }
        });
    }
    const fieldPropsProxy = new Proxy({}, {
        get(target, name) {
            const props = {
                id: name,
                name,
                value: valuesProxy[name],
                onChange: updateValuesProxy[name],
                disabled: loading,
                onBlur: handleBlur[name],
                error: errors[name],
                onError: updateErrors[name],
                onValidating: updateValidating[name],
                validating: validating[name],
            };
            if (!noPositive) {
                props.positive = positive[name];
            }
            return props;
        }
    });
    return {
        fieldProps: fieldPropsProxy,
        submit,
        values: valuesProxy,
        updateValues: updateValuesProxy,
        loading,
        errors,
        updateErrors,
        validating,
        updateValidating,
        touched,
        hasErrors,
        hasFieldErrors,
        anyValidating,
        notReady: hasErrors || anyValidating || loading,
    };
}
exports.useForm = useForm;
//# sourceMappingURL=useForm.js.map